/* ==========================================================================
   BILDPOLSTER-AUSGLEICH
   ---------------------------------------------------------------------------
   Produktbilder des Katalogs sind transparente PNGs, deren Motiv normalerweise
   bis an den Dateirand geht ("hoehen-normiert": 800px hoch, Breite variabel).
   Einzelne Artikel haben Dateien, in denen das Motiv verkleinert und zentriert
   auf einer groesseren, meist quadratischen Leinwand sitzt - Stand 11.09.2026
   rund 600 Artikel, das Motiv teils nur 33 % der Hoehe.

   Jedes Bildfeld im Theme passt die LEINWAND ein, nicht das Motiv. Der leere
   Rand wird also mitskaliert, und derselbe Karton erscheint bis zu dreimal
   kleiner als beim Nachbarartikel.

   Dieses Skript misst je Datei die tatsaechliche Motivflaeche ueber den
   Alpha-Kanal und vergroessert das Bild um den Kehrwert seines Fuellgrades.
   Ergebnis ist pixelgleich zu einem Zuschnitt der Datei; der transparente
   Ueberstand wird vom Rahmen abgeschnitten, vom Motiv geht nichts verloren.

   WARUM NICHT IN CSS. Liquid und CSS kennen nur die Leinwandmasse
   (image.width/height). Wie viel davon Motiv ist, steht in keinem Feld - das
   sieht man erst an den Pixeln. Eine Regel "quadratisch = vergroessern" waere
   falsch: es gibt korrekte quadratische Bilder (Universalpapier, Hersteller-
   fotos), die dabei beschnitten wuerden. Im Browser ist die Information
   lesbar, weil die Shopify-Bild-CDN `access-control-allow-origin: *` sendet.

   EIGENSCHAFTEN
   - Datenfrei: keine Dateiliste, gilt fuer jedes aktuelle und kuenftige Bild.
   - Selbstabschaltend: ab SCHWELLE Fuellgrad wird nichts angefasst. Sind die
     Dateien zugeschnitten, tut das Skript nichts mehr und kann ersatzlos weg.
   - Sparsam: gemessen wird erst kurz vor Sichtbarkeit, je Datei genau einmal
     (Ergebnis im sessionStorage, Schluessel ohne Query - Karte, Produktseite
     und alle srcset-Breiten teilen sich eine Messung), und geladen wird dafuer
     eine 64px-Fassung von rund 2 KB, nicht das angezeigte Bild.
   - Folgenlos bei Ausfall: ohne JS, ohne CORS oder ohne Canvas bleibt alles
     unveraendert. Es wird nur `transform` gesetzt, nie Layout - kein CLS.

   ⚠️ NOTMASSNAHME AN DER FALSCHEN STELLE. Der Shop gleicht hier kaputte
   Bilddaten aus. Repariert gehoert der Bild-Export (Zuschnitt auf die
   Alpha-Bounding-Box); Feeds, Marktplaetze und die Legacy-Shops bekommen die
   Datei selbst und sehen von diesem Skript nichts.
   Hintergrund, Messmethode und Artikellisten:
   docs/produktbild-canvas-und-kartenbild.md
   ========================================================================== */
(function () {
  'use strict';

  var SCHWELLE   = 0.95;   // ab diesem Fuellgrad gilt eine Datei als korrekt
  var RASTER     = 48;     /* Messaufloesung UND Breite des geladenen Messbildes.
                              48 statt 64 halbiert den Messverkehr (2,6-6,2 KB statt
                              3,4-9,9 KB je Datei) und kostet nur Rasterfeinheit:
                              2,1 % statt 1,6 %. Fuer die Entscheidung an der
                              95-%-Schwelle und den Massstab ist das ohne Belang -
                              gegengeprueft an einem gepolsterten, einem korrekten
                              und einem quadratisch-korrekten Bild. */
  var ALPHA_MIN  = 16;     // ab diesem Alpha zaehlt ein Pixel als Motiv
  var MAX_FAKTOR = 3.5;    // Deckel gegen Ausreisser
  var MIN_KANTE  = 32;     // kleinere Bilder (Icons, Mini-Thumbs) bleiben unberuehrt
  var VORLAUF    = '300px';// so frueh vor dem Sichtbarwerden wird gemessen

  /* Behandelt wird jedes Bild aus der Shopify-Dateiablage, ausser in den
     folgenden Bereichen. Bewusst eine Sperr- und keine Erlaubnisliste: eine
     Erlaubnisliste laesst jede neue Komponente stillschweigend durchfallen.
     Die SCHWELLE schuetzt Grafiken ohnehin - Promo-Kacheln, Magazin- und
     Herstellerbilder sind randabfuellend oder haben keinen Alpha-Kanal und
     messen deshalb 100 %. `data-no-bildausgleich` schliesst punktuell aus. */
  var QUELLE = '/cdn/shop/files/';
  var TABU   = '[data-no-bildausgleich],header,footer,nav,[class*="promo"],' +
               '[class*="logo"],[class*="badge"],[class*="article"],' +
               '[class*="magazin"],[class*="hero"],[class*="slideshow"]';

  if (!window.createImageBitmap || !window.fetch || !window.IntersectionObserver) return;

  var ctx;
  try {
    var cv = document.createElement('canvas');
    cv.width = cv.height = RASTER;
    ctx = cv.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
  } catch (e) { return; }

  var speicher = {};        // Dateipfad -> {fb,fh,vx,vy} | null
  var laufend  = {};        // Dateipfad -> Promise
  var platziert = [];       // {bild, feld, mass} fuer den ResizeObserver

  /* Schluessel ohne Query: dieselbe Datei in 160w, 320w und 800w ergibt
     denselben Fuellgrad und wird nur einmal gemessen. */
  function schluessel(url) { return url.split('?')[0]; }

  /* ⚠️ Gemessen wird NICHT die angezeigte Datei, sondern eine RASTER-px-Fassung.
     Ein `fetch` im CORS-Modus kann den Cache-Eintrag des <img> nicht mitbenutzen
     - der Browser laedt die Datei sonst ein ZWEITES Mal, und zwar die grosse:
     gemessen: 2,85 MB Zusatzverkehr fuer 14 Bilder auf einer Kategorieseite
     (die dortigen Vollbilder), gegen ~3-6 KB je Datei bei `width=48` - und das
     Ergebnis ist identisch, denn die Bounding-Box wird ohnehin am RASTER bestimmt. */
  function messUrl(url) {
    var teile = url.split('?');
    var v = /(?:^|&)v=([^&]*)/.exec(teile[1] || '');
    return teile[0] + '?width=' + RASTER + (v ? '&v=' + v[1] : '');
  }

  function ausCache(k) {
    if (k in speicher) return speicher[k];
    try {
      var roh = sessionStorage.getItem('bp:' + k);
      if (roh) return (speicher[k] = JSON.parse(roh));
    } catch (e) {}
    return undefined;
  }

  function inCache(k, wert) {
    speicher[k] = wert;
    try { sessionStorage.setItem('bp:' + k, JSON.stringify(wert)); } catch (e) {}
  }

  function messen(url) {
    var k = ausCache(schluessel(url));
    if (k !== undefined) return Promise.resolve(k);
    var pfad = schluessel(url);
    if (laufend[pfad]) return laufend[pfad];

    laufend[pfad] = fetch(messUrl(url), { mode: 'cors', credentials: 'omit' })
      .then(function (r) { return r.ok ? r.blob() : Promise.reject(); })
      .then(createImageBitmap)
      .then(function (bmp) {
        ctx.clearRect(0, 0, RASTER, RASTER);
        ctx.drawImage(bmp, 0, 0, RASTER, RASTER);
        if (bmp.close) bmp.close();
        var d = ctx.getImageData(0, 0, RASTER, RASTER).data;
        var x0 = RASTER, y0 = RASTER, x1 = -1, y1 = -1;
        for (var y = 0; y < RASTER; y++) {
          for (var x = 0; x < RASTER; x++) {
            if (d[(y * RASTER + x) * 4 + 3] >= ALPHA_MIN) {
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
        }
        /* Ohne Alpha-Kanal misst alles 100 % und wird nie angefasst - richtig
           so, denn dann ist Rand nicht von Motiv zu unterscheiden. */
        var wert = (x1 < 0) ? null : {
          fb: (x1 - x0 + 1) / RASTER,
          fh: (y1 - y0 + 1) / RASTER,
          vx: ((x0 + x1 + 1) / 2 - RASTER / 2) / RASTER,
          vy: ((y0 + y1 + 1) / 2 - RASTER / 2) / RASTER
        };
        inCache(pfad, wert);
        return wert;
      })
      .catch(function () { inCache(pfad, null); return null; })
      .then(function (w) { delete laufend[pfad]; return w; });

    return laufend[pfad];
  }

  /* Die sichtbare Leinwand innerhalb der Elementbox. Bei object-fit:contain
     (Kategoriekarte: Bild liegt absolut im Feld) ist sie kleiner als das
     Element, bei auto-skalierten Bildern identisch. Ohne diese Unterscheidung
     rechnet der Versatz am falschen Mass. */
  function leinwand(bild, ew, eh) {
    var nw = bild.naturalWidth, nh = bild.naturalHeight;
    if (nw && nh && getComputedStyle(bild).objectFit === 'contain') {
      var s = Math.min(ew / nw, eh / nh);
      return { w: nw * s, h: nh * s };
    }
    return { w: ew, h: eh };
  }

  function platzieren(eintrag) {
    var bild = eintrag.bild, m = eintrag.mass;
    var faktor = eintrag.faktor;
    var r = bild.getBoundingClientRect();
    var ew = r.width / (eintrag.gesetzt || 1), eh = r.height / (eintrag.gesetzt || 1);
    if (!ew || !eh) return;

    var l = leinwand(bild, ew, eh);

    /* Der Faktor haengt NUR an der Datei, nicht am umgebenden Feld. Am Feld
       gerechnet schoss er dort ueber, wo das Feld hoeher ist als das Bild
       (Flex-Container mit align-items:flex-start). 1/Fuellgrad ist exakt das,
       was ein Zuschnitt der Datei ergibt - unabhaengig vom Rahmen. */
    /* ⚠️ Die vorhandene Transform des Themes MUSS erhalten bleiben: die
       Kategoriekarte zentriert ihr absolut gesetztes Bild ueber
       `.aspect-ratio--short img { transform: translate(-50%,-50%) }`. Wird sie
       ueberschrieben, rutscht jedes Kartenbild aus dem Rahmen. Der eigene
       Anteil kommt deshalb HINTEN dran und wird damit zuerst angewandt. */
    bild.style.transform = (bild.__bpBasis ? bild.__bpBasis + ' ' : '') +
      'translate(' + (-m.vx * l.w * faktor).toFixed(1) + 'px,' +
      (-m.vy * l.h * faktor).toFixed(1) + 'px) scale(' + faktor.toFixed(3) + ')';
    eintrag.gesetzt = faktor;
  }

  var groessenwache = window.ResizeObserver ? new ResizeObserver(function (eintraege) {
    eintraege.forEach(function (e) {
      var t = e.target.__bpEintrag;
      if (t) platzieren(t);
    });
  }) : null;

  function anwenden(bild, m) {
    var faktor = 1 / Math.max(m.fb, m.fh);
    faktor = Math.min(faktor, MAX_FAKTOR);
    if (faktor <= 1.01) return;

    /* Ueberstand abschneiden statt ueberlappen zu lassen - sonst liegt eine
       unsichtbare Bildflaeche ueber der Nachbarspalte und faengt Klicks ab. */
    var rahmen = bild.parentElement;
    if (rahmen && getComputedStyle(rahmen).overflow === 'visible') {
      rahmen.style.overflow = 'hidden';
    }

    var eintrag = { bild: bild, mass: m, faktor: faktor, gesetzt: 1 };
    platzieren(eintrag);
    bild.setAttribute('data-bildpolster', Math.round(Math.max(m.fb, m.fh) * 100));

    if (groessenwache && rahmen) {
      rahmen.__bpEintrag = eintrag;
      groessenwache.observe(rahmen);
    }
  }

  function behandelbar(bild) {
    var url = bild.currentSrc || bild.src || '';
    if (url.indexOf(QUELLE) === -1) return false;
    var r = bild.getBoundingClientRect();
    if (r.width < MIN_KANTE || r.height < MIN_KANTE) return false;
    return !bild.closest(TABU);
  }

  function pruefen(bild) {
    if (bild.__bpFertig || !behandelbar(bild)) return;
    bild.__bpFertig = true;
    /* Transform des Themes sichern, solange noch nichts inline steht. */
    var vorhanden = getComputedStyle(bild).transform;
    bild.__bpBasis = (vorhanden && vorhanden !== 'none') ? vorhanden : '';
    messen(bild.currentSrc || bild.src).then(function (m) {
      if (m && Math.max(m.fb, m.fh) < SCHWELLE) anwenden(bild, m);
    });
  }

  var sichtwache = new IntersectionObserver(function (eintraege) {
    eintraege.forEach(function (e) {
      if (!e.isIntersecting) return;
      sichtwache.unobserve(e.target);
      var bild = e.target;
      if (bild.complete && bild.naturalWidth) pruefen(bild);
      else bild.addEventListener('load', function () { pruefen(bild); }, { once: true });
    });
  }, { rootMargin: VORLAUF });

  function einsammeln(wurzel) {
    if (!wurzel.querySelectorAll) return;
    var liste = [].slice.call(wurzel.querySelectorAll('img'));
    if (wurzel.tagName === 'IMG') liste.push(wurzel);
    liste.forEach(function (bild) {
      if (!bild.__bpBeobachtet) { bild.__bpBeobachtet = true; sichtwache.observe(bild); }
    });
  }

  function start() {
    einsammeln(document);
    /* Karussell, Quick-View und Nachladen liefern Bilder spaeter nach. */
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var n = muts[i].addedNodes;
          for (var j = 0; j < n.length; j++) if (n[j].nodeType === 1) einsammeln(n[j]);
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
